import { IComponentController } from 'angular';
import { IUrlConfig } from 'typings/core/config/url';
import { IRegistrationErrorResponse } from 'typings/auth/services/registration';
import {
  RegistrationFlowStateName,
  StateParamsPlatform,
  EasyshipLeadStateName,
  RegistrationFlowStep,
} from '@client/auth/data/state';
import { IApiKeysConfig } from 'typings/core/config/api-keys';
import { IApiConfig } from 'typings/core/config/api';
import {
  SIGNUP_REDIRECTION_DELAY,
  StepsNavigationService,
} from '@client/auth/global/services/steps-navigation/steps-navigation.service';
import { BillingFrequency, DefinedSubscriptionPlanId } from '@client/data/subscription';
import { ShopifyAuthService } from '@client/auth/global/services/shopify-auth/shopify-auth.service';
import { EasyshipLocale } from '@client/core/corelogic/models/EasyshipLocale';

import { MixpanelService } from '@client/core/services/mixpanel/mixpanel.service';
import { toastError } from '@client/core/components/react/Toastify';
import { IAuthService } from 'typings/core/services/auth';
import { OtpTimeTypes } from '@app/pages/registration/components/VerifyOtp/types';
import { debounce } from 'lodash';
import template from './signup.html?raw';
import style from './signup.module.scss';
import { RegistrationService } from '../global/services/registration/registration.service';
import { EMAIL_DOMAIN_LISTS } from './constants';

interface Language {
  name: string;
  enabled: boolean;
}

export interface LanguageOption {
  code: EasyshipLocale;
  name: string;
}

class SignupController implements IComponentController {
  recaptchaKeys: string;
  style = style;
  busy = false;
  password = '';
  recaptchaWidgetId = 0;
  isLoginForm = false;
  locale = 'en';
  languageOptions: LanguageOption[];
  showShopifyCta = false;
  platform = null;
  businessEmailDetected = true;

  static $inject = [
    '$scope',
    '$state',
    '$window',
    '$location',
    '$cookies',
    '$timeout',
    '$translate',
    'vcRecaptchaService',
    'MixpanelService',
    'Auth',
    'GoogleAnalytics',
    'RegistrationService',
    'StepsNavigationService',
    'ShopifyAuthService',
    'UrlPath',
    'API',
    'ApiKeys',
  ];
  constructor(
    private $scope: ng.IScope,
    public $state: ng.ui.IStateService,
    public $window: ng.IWindowService,
    public $location: ng.ILocationService,
    private $cookies: angular.cookies.ICookiesService,
    private $timeout: ng.ITimeoutService,
    private $translate: angular.translate.ITranslateService,
    public vcRecaptchaService: any,
    private MixpanelService: MixpanelService,
    private Auth: IAuthService,
    private GoogleAnalytics: any,
    public RegistrationService: RegistrationService,
    public StepsNavigationService: StepsNavigationService,
    public ShopifyAuthService: ShopifyAuthService,
    private UrlPath: IUrlConfig,
    public API: IApiConfig,
    private ApiKeys: IApiKeysConfig
  ) {
    this.recaptchaKeys = this.ApiKeys.recaptcha;
    const languages: Record<EasyshipLocale, Language> = {
      en: { name: 'English', enabled: true },
      fr: { name: 'Français', enabled: true },
      de: { name: 'Deutsch', enabled: true },
      es: { name: 'Español', enabled: true },
    };
    this.languageOptions = Object.entries(languages).reduce<LanguageOption[]>(
      (options, [key, lang]) =>
        lang.enabled ? [...options, { code: key as EasyshipLocale, name: lang.name }] : options,
      []
    );
    this.platform = this.$state.params.platform;
  }

  $onInit() {
    const urlParams = new URLSearchParams(this.$window.location.search);

    const irClickIdKey = this.RegistrationService.impactIntegrationCookieName.IrClickId;

    const irClickId = urlParams.get(irClickIdKey);
    const mediaId = urlParams.get('utm_medium') === 'affiliate' && urlParams.get('utm_campaign');

    if (irClickId) {
      this.$cookies.put(irClickIdKey, irClickId);
    }

    if (mediaId) {
      this.$cookies.put(this.RegistrationService.impactIntegrationCookieName.MediaId, mediaId);
    }

    if (!this.$state.params.platform) {
      // remove OTP send/resend button related on form change, making sure that when user change email, it will send again for the first time EXCEPT platform
      // because platform doesn't change it's registration ID
      this.$window.localStorage.removeItem(OtpTimeTypes.RESEND_ALLOWED_AT);
      this.$window.localStorage.removeItem(OtpTimeTypes.EXPIRES_AT);
    }
  }

  get isFulfillment() {
    return this.$state.params.platform === StateParamsPlatform.Fulfillment;
  }

  get isUberFreight() {
    return this.$state.params.platform === StateParamsPlatform.UberFreight;
  }

  onEmailChange(value: string): void {
    this.RegistrationService.data.user.email = value;

    if (value === '') {
      this.businessEmailDetected = true;
      return;
    }

    const setBusinessEmailDetectedDebounce = debounce(() => {
      if (value.includes('@')) {
        // Email regex pattern
        const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;

        const match = value.match(emailPattern);
        if (match) {
          const domain = match[1].toLowerCase();
          this.businessEmailDetected = !EMAIL_DOMAIN_LISTS.includes(domain);
        } else {
          this.businessEmailDetected = false;
        }
      }
      this.$scope.$evalAsync();
    }, 250);

    setBusinessEmailDetectedDebounce();
  }

  onPasswordChange(value: string): void {
    this.password = value;
  }

  setLocale(locale: string) {
    this.locale = locale;
    this.$translate.use(locale);
  }

  setRecaptchaWidgetId(recaptchaWidgetId: number): void {
    this.recaptchaWidgetId = recaptchaWidgetId;
  }

  switchForm(): void {
    const mixpanelParams = {
      platform_name: this.platformName,
    };

    if (this.$state.current.name === EasyshipLeadStateName) {
      this.MixpanelService.track('Signup - Email Step - Redirect to login page', mixpanelParams);
      this.$window.location.href = this.UrlPath.LOGIN;
    } else if (this.platformName === 'uber_freight') {
      this.$window.location.href = '/uber-freight/login';
    } else {
      this.isLoginForm = !this.isLoginForm;
    }
  }

  validateForm(form: ng.IFormController): void {
    if (form.$error.ngEmail && form.$error.ngEmail[0]?.$invalid) {
      return;
    }

    if (form.$error.required) {
      toastError(this.$translate.instant('toast.incomplete-form'));
      return;
    }

    if (form.$error.recaptcha) {
      this.vcRecaptchaService.execute();
      return;
    }

    this.authenticate();
  }

  signup(recaptchaResponse?: string): void {
    this.busy = true;

    // TODO: Make this DRYish
    if (this.$state.params.id) {
      const params = {
        id: this.$state.params.id,
        platform: this.RegistrationService.getBackendPlatformName(this.$state.params.platform),
        step: RegistrationFlowStep.User,
        complete: false,
      };

      this.RegistrationService.update(params)
        .then(({ registration }) => {
          this.StepsNavigationService.next(registration.fields_by_steps);

          this.$state.go(`${RegistrationFlowStateName}.${this.StepsNavigationService.firstStep}`, {
            platform: this.$state.params.platform,
            id: registration.id,
          });
        })
        .catch((errorResponse: IRegistrationErrorResponse) => {
          toastError(
            this.$translate.instant('toast.update-error', {
              noun: this.$translate.instant('global.registration').toLowerCase(),
              details: this.RegistrationService.formatErrors(errorResponse),
            })
          );

          this.reloadRecaptcha();
        })
        .finally(() => {
          this.busy = false;
        });
    } else {
      const plan: DefinedSubscriptionPlanId | undefined =
        parseInt(this.$state.params.plan, 10) || undefined;
      const billingFrequency: BillingFrequency | undefined = this.$state.params.billing_frequency;

      this.RegistrationService.signup(recaptchaResponse, undefined, plan, billingFrequency)
        .then(({ registration }) => {
          this.GoogleAnalytics.createEvent({
            event: 'gaEvent',
            eventCategory: 'User',
            eventAction: 'Signup Started',
            eventLabel: 'Easyship Start Signup',
          });

          this.StepsNavigationService.updateAccessibleAndFieldBySteps(registration.fields_by_steps);
          this.$state.go(`${RegistrationFlowStateName}.${this.StepsNavigationService.firstStep}`, {
            platform: this.$state.params.platform || StateParamsPlatform.Dashboard,
            id: registration.id,
            plan: plan || null,
            billingFrequency: billingFrequency || null,
          });

          if (this.$state.params.platform === StateParamsPlatform.Fulfillment) {
            this.sendEfulfillmentGAEvent();
          }
        })
        .catch((errorResponse: IRegistrationErrorResponse) => {
          toastError(
            this.$translate.instant('toast.update-error', {
              noun: this.$translate.instant('global.registration').toLowerCase(),
              details: this.RegistrationService.formatErrors(errorResponse),
            })
          );

          this.reloadRecaptcha();
        })
        .finally(() => {
          this.busy = false;
        });
    }
  }

  login(recaptchaResponse?: string): void {
    this.busy = true;
    const params = {
      id: this.$state.params.id,
      platform: this.platformName,
    };

    this.RegistrationService.login(params, this.password, recaptchaResponse)
      .then(({ registration, session_token, redirect_url }) => {
        if (!session_token) {
          toastError(this.$translate.instant('toast.default-error'));
          return;
        }

        this.Auth.setCredentials(session_token);
        this.$cookies.putObject('ES_USED_DASHBOARD', true, { domain: 'easyship.com' });

        if (redirect_url) {
          switch (this.$state.params.platform) {
            case StateParamsPlatform.Cashier:
              this.ShopifyAuthService.handleBackendRedirectUrl(session_token, redirect_url);
              break;

            case StateParamsPlatform.Square: {
              const parsedUrl = new URL(redirect_url);
              const squareState = parsedUrl.searchParams.get('state');
              if (squareState) this.$window.localStorage.setItem('squareState', squareState);

              break;
            }
            default:
              break;
          }

          this.MixpanelService.track('Signup - Email Step - Logging in', {
            platform_name: this.platformName,
          });

          this.$timeout(() => {
            this.$window.location.href = redirect_url;
          }, SIGNUP_REDIRECTION_DELAY);

          return;
        }

        if (registration.complete) {
          const isNewStore = !!registration.data?.store.id;

          this.StepsNavigationService.goToAuthenticatedPage(
            session_token,
            isNewStore ? '&new_connect' : ''
          );
          return;
        }

        const currentStepStateName = `${RegistrationFlowStateName}.${registration.current_step}`;
        this.StepsNavigationService.updateAccessibleAndFieldBySteps(registration.fields_by_steps);
        this.StepsNavigationService.lastStepReached = registration.last_step_reached;

        this.$state.go(currentStepStateName, {
          platform: this.$state.params.platform,
          id: registration.id,
        });
      })
      .catch((errorResponse: IRegistrationErrorResponse) => {
        toastError(
          this.$translate.instant('toast.update-error', {
            noun: this.$translate.instant('global.registration').toLowerCase(),
            details: this.RegistrationService.formatErrors(errorResponse),
          })
        );

        this.reloadRecaptcha();
      })
      .finally(() => {
        this.busy = false;
      });
  }

  authenticate(recaptchaResponse?: string): void {
    if (this.isLoginForm) {
      this.login(recaptchaResponse);
    } else {
      this.signup(recaptchaResponse);
    }
  }

  goToResetPassword(): void {
    this.$window.location.href = this.UrlPath.RESET_PASSWORD;
  }

  get platformName(): string {
    const platform =
      (this.$state.params && this.$state.params.platform) || StateParamsPlatform.Dashboard;
    return this.RegistrationService.getBackendPlatformName(platform);
  }

  private reloadRecaptcha() {
    this.vcRecaptchaService.reload(this.recaptchaWidgetId);
  }

  private sendEfulfillmentGAEvent() {
    this.GoogleAnalytics.createEvent({
      event: 'gaEvent',
      eventCategory: 'User',
      eventAction: 'Lead',
      eventLabel: 'Fulfillment form',
    });
  }
}

const SignupComponent = {
  controller: SignupController,
  template,
};

export { SignupComponent };
